import { Layer as DeckGlLayer } from "@deck.gl/core";
import i18n from "i18next";
import { ReactNode } from "react";

import {
  api,
  type Location,
  type ObservationsMapParameterDisplayGroup,
  type ParameterTimeSeriesConfiguration,
  type ParametersLocation,
  type SteppedTimeRange,
} from "@/lib/api";
import TimeRange from "@/types/time-range";
import { Dimension, DimensionValueSet } from "@/lib/dimensions";
import {
  dynamicDimensionsContainValues,
  getRelevantDimensionValues,
  getTimeDimensionFromTemporalExtent,
} from "@/lib/dimensions/helpers";
import {
  deriveSteppedTimeRangeFromDatapoints,
  steppedTimeRangesEqual,
  toSteppedTenMinuteRange,
} from "@/features/shared/widgets/observations-map/derive-stepped-time-range-from-datapoints";
import {
  filterParametersLocationsByDashboardLinks,
  orderStationsByPriority,
} from "@/features/shared/widgets/observations-map/filter-stations-by-dashboard-locations";
import Layer from "@/lib/map-layers/layer";
import { LayerSettingValueSet } from "@/lib/map-layers/layer-settings";
import { EnumLayerSetting } from "@/lib/map-layers/layer-settings/enum-layer-setting";
import { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";

import {
  getTimeSeriesParameterEntries,
  resolveDisplayGroupLabel,
} from "./parameter-display-group-label";
import { StationMarkersLayerView } from "./station-markers-layer-view";

export type { StationMarkersInteraction } from "./station-markers-interaction-context";

export const OBSERVED_STATION_MARKERS_LAYER_ID = "observed-station-markers";

export const observedStationMarkersLayerControllerId = `layer-${OBSERVED_STATION_MARKERS_LAYER_ID}`;

const DISPLAY_GROUP_LAYER_SETTING_ID = "display_group";

export type ObservedStationMarkersLayerOptions = {
  showAllLocations: boolean;
  dashboardLocations?: Location[];
};

export class ObservedStationMarkersLayer implements Layer {
  private locations: ParametersLocation[] = [];
  private locationsLoaded = false;
  private loadedDimensions: Dimension[] = [];
  private lastAppliedRange: SteppedTimeRange | null = null;
  private onDimensionsChanged?: () => void;
  private displayGroups: ObservationsMapParameterDisplayGroup[];
  private timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  private catalogLabel: (parameterId: string) => string | undefined;
  private showAllLocations: boolean;
  private dashboardLocations?: Location[];

  constructor(
    displayGroups: ObservationsMapParameterDisplayGroup[],
    timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
    catalogLabel: (parameterId: string) => string | undefined = () => undefined,
    options: ObservedStationMarkersLayerOptions = { showAllLocations: true },
  ) {
    this.displayGroups = displayGroups;
    this.timeSeriesConfigurations = timeSeriesConfigurations;
    this.catalogLabel = catalogLabel;
    this.showAllLocations = options.showAllLocations;
    this.dashboardLocations = options.dashboardLocations;
  }

  get id() {
    return OBSERVED_STATION_MARKERS_LAYER_ID;
  }

  getTitle(_dimensionValues: DimensionValueSet) {
    return i18n.t("map.observed_stations_layer_title");
  }

  get dimensions() {
    return this.loadedDimensions;
  }

  get settings() {
    if (this.displayGroups.length <= 0) return [];

    const entries = getTimeSeriesParameterEntries(this.timeSeriesConfigurations);
    const values = this.displayGroups.map((group, index) => ({
      key: `display_group_${index}`,
      label:
        resolveDisplayGroupLabel(group, entries, this.catalogLabel) ||
        i18n.t("admin.widgets.fields.model_map.general.untitled"),
      value: group,
    }));

    return [
      new EnumLayerSetting<ObservationsMapParameterDisplayGroup>({
        id: DISPLAY_GROUP_LAYER_SETTING_ID,
        values,
        default: values[0],
      }),
    ];
  }

  getRelevantDimensionValues(values: DimensionValueSet): DimensionValueSet {
    return getRelevantDimensionValues(this.loadedDimensions, values);
  }

  setOnDimensionsChanged(listener: () => void) {
    this.onDimensionsChanged = listener;
  }

  applyDerivedTimeDimension(timestamps: string[]) {
    const derived = deriveSteppedTimeRangeFromDatapoints(timestamps);
    if (!derived) return;
    if (this.lastAppliedRange && steppedTimeRangesEqual(derived, this.lastAppliedRange)) {
      return;
    }

    this.lastAppliedRange = derived;
    this.loadedDimensions = getTimeDimensionFromTemporalExtent(derived);
    this.onDimensionsChanged?.();
  }

  async initialize(timeRange: TimeRange) {
    this.locationsLoaded = false;
    const response = await api.parameters.listLocations();
    const selectedLocations = this.showAllLocations
      ? response.data
      : filterParametersLocationsByDashboardLinks(response.data, this.dashboardLocations);
    // Order by priority in both modes so higher-priority stations render first
    // (dashboard order wins, then the standard list, then a stable fallback).
    this.locations = orderStationsByPriority(selectedLocations, this.dashboardLocations);
    this.locationsLoaded = true;
    const placeholderRange = toSteppedTenMinuteRange(timeRange);
    this.lastAppliedRange = placeholderRange;
    this.loadedDimensions = getTimeDimensionFromTemporalExtent(placeholderRange);
  }

  async preload(
    _dimensionValues: DimensionValueSet,
    _priority: number,
    _signal?: AbortSignal,
  ) {
    return;
  }

  isLoaded(_dimensionValues: DimensionValueSet) {
    return this.locationsLoaded;
  }

  isActive(dimensionValues: DimensionValueSet) {
    if (this.loadedDimensions.length === 0) return false;
    return dynamicDimensionsContainValues(this.loadedDimensions, dimensionValues);
  }

  isForecast(_dimensionValues: DimensionValueSet) {
    return false;
  }

  renderLayer(
    _dimensionValues: DimensionValueSet,
    _settingValues: LayerSettingValueSet,
  ): DeckGlLayer | undefined {
    return undefined;
  }

  renderLegend(
    _dimensionValues: DimensionValueSet,
    _settingValues: LayerSettingValueSet,
  ): ReactNode {
    return undefined;
  }

  renderComponents(
    currentDimensionValues: DimensionValueSet,
    settingValues: LayerSettingValueSet,
  ): ReactNode {
    if (!this.isLoaded(currentDimensionValues)) return undefined;
    if (!this.isActive(currentDimensionValues)) return undefined;

    const relevant = this.getRelevantDimensionValues(currentDimensionValues);
    const selectedGroup =
      (settingValues[DISPLAY_GROUP_LAYER_SETTING_ID]?.value as
        | ObservationsMapParameterDisplayGroup
        | undefined) ?? this.displayGroups[0];

    return (
      <StationMarkersLayerView
        locations={this.locations}
        currentTime={relevant.time as TimeRangeDimensionValue | undefined}
        activeDisplayGroup={selectedGroup}
        timeSeriesConfigurations={this.timeSeriesConfigurations}
        resolveParameterLabel={this.catalogLabel}
        onDerivedTimestamps={(timestamps) => this.applyDerivedTimeDimension(timestamps)}
      />
    );
  }

  clearLoadFailures(): void {
    // no-op
  }

  hasLoadError(): boolean {
    return false;
  }

  hasPartialLoadErrors(): boolean {
    return false;
  }
}
